import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

/**
 * PublishModal - Shared publish dialog for all creation studios
 * 
 * Supports publishing to:
 * - Art Feed
 * - Music Feed  
 * - Stories Feed
 * - General Feed
 * 
 * With options for:
 * - Community targeting
 * - Cross-media linking (add background music, artwork, etc.)
 * - Visibility settings
 * - Monetization
 */
export default function PublishModal({
  isOpen,
  onClose,
  onPublish,
  contentType = 'general', // 'art', 'music', 'story', 'video', 'general'
  mediaData = null, // { file, url, thumbnail, metadata }
  initialTitle = '',
  initialDescription = '',
  initialTags = [],
}) {
  // Form state
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [tags, setTags] = useState(initialTags);
  const [tagInput, setTagInput] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [feedType, setFeedType] = useState(contentType);
  
  // Community selection
  const [communities, setCommunities] = useState([]);
  const [selectedCommunityId, setSelectedCommunityId] = useState(null);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  
  // Cross-media linking
  const [showMusicPicker, setShowMusicPicker] = useState(false);
  const [showArtPicker, setShowArtPicker] = useState(false);
  const [availableMusic, setAvailableMusic] = useState([]);
  const [availableArtwork, setAvailableArtwork] = useState([]);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  
  // Publishing state
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState(null);
  
  // Music-specific
  const [genre, setGenre] = useState('');
  const [bpm, setBpm] = useState('');
  const [keySignature, setKeySignature] = useState('');
  const [allowUseInVideos, setAllowUseInVideos] = useState(true);
  const [allowRemix, setAllowRemix] = useState(false);
  
  // Monetization
  const [isMonetized, setIsMonetized] = useState(false);
  const [priceCents, setPriceCents] = useState(0);
  const [isDownloadable, setIsDownloadable] = useState(false);

  // Load user's communities on mount
  useEffect(() => {
    if (isOpen) {
      loadCommunities();
    }
  }, [isOpen]);

  const loadCommunities = async () => {
    setLoadingCommunities(true);
    try {
      const response = await apiService.getUserCommunities();
      setCommunities(response.communities || []);
    } catch (err) {
      console.error('Failed to load communities:', err);
    } finally {
      setLoadingCommunities(false);
    }
  };

  const loadAvailableMusic = async () => {
    try {
      const response = await apiService.getAvailableMusic({ limit: 50 });
      setAvailableMusic(response.tracks || []);
      setShowMusicPicker(true);
    } catch (err) {
      console.error('Failed to load music:', err);
    }
  };

  const loadAvailableArtwork = async () => {
    try {
      const response = await apiService.getAvailableArtwork({ limit: 50 });
      setAvailableArtwork(response.artworks || []);
      setShowArtPicker(true);
    } catch (err) {
      console.error('Failed to load artwork:', err);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && tags.length < 10) {
      setTags([...tags, tagInput.trim().toLowerCase().replace(/^#/, '')]);
      setTagInput('');
    }
  };

  const removeTag = (index) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const handlePublish = async (isDraft = false) => {
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    setIsPublishing(true);
    setError(null);

    try {
      // Build the publish data based on content type
      const publishData = {
        title,
        description,
        tags,
        visibility: isDraft ? 'draft' : visibility,
        feed_type: feedType,
        community_id: selectedCommunityId,
        is_monetized: isMonetized,
        price_cents: priceCents,
        is_downloadable: isDownloadable,
      };

      // Add cross-media links
      if (selectedMusic) {
        publishData.background_audio_id = selectedMusic.id;
        publishData.linked_content = [
          ...(publishData.linked_content || []),
          { type: 'music', id: selectedMusic.id.toString(), usage: 'background_audio' }
        ];
      }

      if (selectedArtwork && contentType !== 'art') {
        publishData.artwork_id = selectedArtwork.id;
        publishData.linked_content = [
          ...(publishData.linked_content || []),
          { type: 'artwork', id: selectedArtwork.id, usage: 'attachment' }
        ];
      }

      // Add music-specific data
      if (contentType === 'music') {
        publishData.genre = genre;
        publishData.bpm = bpm ? parseInt(bpm) : null;
        publishData.key = keySignature;
        publishData.allow_use_in_videos = allowUseInVideos;
        publishData.allow_remix = allowRemix;
      }

      // Call the onPublish callback with all data
      await onPublish(publishData, mediaData);
      
      onClose();
    } catch (err) {
      console.error('Publish failed:', err);
      setError(err.message || 'Failed to publish. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-slate-900 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto border border-white/10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">
            Publish {contentType === 'art' ? 'Artwork' : contentType === 'music' ? 'Track' : contentType === 'story' ? 'Story' : 'Content'}
          </h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Title */}
          <div>
            <label className="text-sm text-slate-400 block mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give your content a name"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm text-slate-400 block mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell us about your creation..."
              rows={3}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 resize-none focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Music-specific fields */}
          {contentType === 'music' && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Genre</label>
                <select
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2"
                >
                  <option value="">Select genre</option>
                  <option value="hip-hop">Hip-Hop</option>
                  <option value="electronic">Electronic</option>
                  <option value="pop">Pop</option>
                  <option value="rock">Rock</option>
                  <option value="r&b">R&B</option>
                  <option value="jazz">Jazz</option>
                  <option value="classical">Classical</option>
                  <option value="ambient">Ambient</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">BPM</label>
                <input
                  type="number"
                  value={bpm}
                  onChange={(e) => setBpm(e.target.value)}
                  placeholder="120"
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Key</label>
                <select
                  value={keySignature}
                  onChange={(e) => setKeySignature(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2"
                >
                  <option value="">Select key</option>
                  <option value="C">C Major</option>
                  <option value="Am">A Minor</option>
                  <option value="G">G Major</option>
                  <option value="Em">E Minor</option>
                  <option value="D">D Major</option>
                  <option value="Bm">B Minor</option>
                  <option value="F">F Major</option>
                  <option value="Dm">D Minor</option>
                </select>
              </div>
            </div>
          )}

          {/* Tags */}
          <div>
            <label className="text-sm text-slate-400 block mb-1">Tags ({tags.length}/10)</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Add a tag"
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2"
              />
              <button
                onClick={addTag}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg"
              >
                +
              </button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {tags.map((tag, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs flex items-center gap-1"
                  >
                    #{tag}
                    <button onClick={() => removeTag(i)} className="hover:text-white">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Feed Type */}
          <div>
            <label className="text-sm text-slate-400 block mb-1">Publish To</label>
            <div className="flex flex-wrap gap-2">
              {['general', 'art', 'music', 'stories', 'video'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFeedType(type)}
                  className={`px-3 py-1.5 rounded-full text-sm capitalize transition-colors ${
                    feedType === type
                      ? 'bg-purple-500 text-white'
                      : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {type === 'general' ? 'Main Feed' : `${type} Feed`}
                </button>
              ))}
            </div>
          </div>

          {/* Community Selection */}
          <div>
            <label className="text-sm text-slate-400 block mb-1">Community (optional)</label>
            <select
              value={selectedCommunityId || ''}
              onChange={(e) => setSelectedCommunityId(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2"
            >
              <option value="">Post to your profile</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
            </select>
          </div>

          {/* Cross-Media Linking - Only show for video/general posts */}
          {(contentType === 'video' || contentType === 'general') && (
            <div>
              <label className="text-sm text-slate-400 block mb-2">Add Media</label>
              <div className="flex gap-2">
                <button
                  onClick={loadAvailableMusic}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm flex items-center justify-center gap-2 ${
                    selectedMusic ? 'bg-purple-500/30 text-purple-300' : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                  </svg>
                  {selectedMusic ? selectedMusic.title : 'Add Music'}
                </button>
                <button
                  onClick={loadAvailableArtwork}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm flex items-center justify-center gap-2 ${
                    selectedArtwork ? 'bg-emerald-500/30 text-emerald-300' : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {selectedArtwork ? 'Art Added' : 'Add Art'}
                </button>
              </div>
            </div>
          )}

          {/* Music Usage Permissions */}
          {contentType === 'music' && (
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allowUseInVideos}
                  onChange={(e) => setAllowUseInVideos(e.target.checked)}
                  className="rounded bg-white/10"
                />
                <span className="text-sm">Allow others to use in their videos</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allowRemix}
                  onChange={(e) => setAllowRemix(e.target.checked)}
                  className="rounded bg-white/10"
                />
                <span className="text-sm">Allow remixes</span>
              </label>
            </div>
          )}

          {/* Visibility */}
          <div>
            <label className="text-sm text-slate-400 block mb-1">Visibility</label>
            <div className="flex gap-2">
              {[
                { value: 'public', label: 'Public', icon: '🌍' },
                { value: 'followers', label: 'Followers', icon: '👥' },
                { value: 'private', label: 'Private', icon: '🔒' },
              ].map(({ value, label, icon }) => (
                <button
                  key={value}
                  onClick={() => setVisibility(value)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm flex items-center justify-center gap-2 ${
                    visibility === value
                      ? 'bg-purple-500 text-white'
                      : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {icon} {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => handlePublish(true)}
            disabled={isPublishing}
            className="flex-1 px-4 py-3 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors"
          >
            Save Draft
          </button>
          <button
            onClick={() => handlePublish(false)}
            disabled={isPublishing}
            className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 font-bold hover:from-purple-600 hover:to-pink-600 transition-colors"
          >
            {isPublishing ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </div>

      {/* Music Picker Modal */}
      {showMusicPicker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80">
          <div className="bg-slate-800 rounded-xl p-4 w-full max-w-md max-h-[70vh] overflow-y-auto border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold">Select Background Music</h3>
              <button onClick={() => setShowMusicPicker(false)} className="p-1 hover:bg-white/10 rounded">×</button>
            </div>
            <div className="space-y-2">
              {availableMusic.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No music available</p>
              ) : (
                availableMusic.map((track) => (
                  <button
                    key={track.id}
                    onClick={() => {
                      setSelectedMusic(track);
                      setShowMusicPicker(false);
                    }}
                    className={`w-full p-3 rounded-lg text-left flex items-center gap-3 transition-colors ${
                      selectedMusic?.id === track.id 
                        ? 'bg-purple-500/30 border border-purple-500' 
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <div className="w-10 h-10 bg-purple-500/20 rounded flex items-center justify-center">
                      🎵
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{track.title}</p>
                      <p className="text-xs text-gray-400">by {track.username || 'Unknown'}</p>
                    </div>
                    {track.duration_ms && (
                      <span className="text-xs text-gray-400">
                        {Math.floor(track.duration_ms / 60000)}:{String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0')}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
            <button
              onClick={() => {
                setSelectedMusic(null);
                setShowMusicPicker(false);
              }}
              className="w-full mt-3 px-4 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Art Picker Modal */}
      {showArtPicker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80">
          <div className="bg-slate-800 rounded-xl p-4 w-full max-w-lg max-h-[70vh] overflow-y-auto border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold">Select Artwork</h3>
              <button onClick={() => setShowArtPicker(false)} className="p-1 hover:bg-white/10 rounded">×</button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {availableArtwork.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4 col-span-3">No artwork available</p>
              ) : (
                availableArtwork.map((art) => (
                  <button
                    key={art.id}
                    onClick={() => {
                      setSelectedArtwork(art);
                      setShowArtPicker(false);
                    }}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedArtwork?.id === art.id 
                        ? 'border-emerald-500' 
                        : 'border-transparent hover:border-white/30'
                    }`}
                  >
                    <img
                      src={art.image_url || art.thumbnail_url}
                      alt={art.title}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))
              )}
            </div>
            <button
              onClick={() => {
                setSelectedArtwork(null);
                setShowArtPicker(false);
              }}
              className="w-full mt-3 px-4 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
